import { gql } from '@apollo/client'

export const GET_PLANS_LIST = gql`
  query GetPlansList(
    $limit: Int!
    $offset: Int!
    $order: [[String]]!
    $where: BillingProviderProductListWhereInput
    $providerNames: [BillingProviderName]!
    $productNames: [String]!
    $searchString: String
  ) {
    planList: billingProviderProductList(
      order: $order
      limit: $limit
      offset: $offset
      where: $where
      billingProduct: { name: { in: $productNames } }
      billingProvider: { name: { in: $providerNames } }
      searchString: $searchString
    ) {
      billingProviderProducts {
        id
        sku
        description
        trialLength
        trialTermType
        primaryStakeholder
        enabled
        createdAt
        updatedAt
        billingProduct {
          id
          name
          platform
          contentSetId
        }
        billingProvider {
          id
          name
        }
        billingProviderProductPrices {
          id
          price
          currency
          currencySymbol
        }
      }
    }
    allPlans: billingProviderProductList(
      limit: 10000
      where: $where
      billingProduct: { name: { in: $productNames } }
      billingProvider: { name: { in: $providerNames } }
      searchString: $searchString
    ) {
      billingProviderProducts {
        id
      }
    }
  }
`

export const GET_PLAN_BY_SKU = gql`
  query GetPlanBySku($sku: String!) {
    billingProviderProductList(where: { sku: { eq: $sku } }) {
      billingProviderProducts {
        id
        sku
        description
        notes
        type
        termLength
        termType
        trialLength
        trialTermType
        taxable
        checkoutEnabledStart
        checkoutEnabledEnd
        enabled
        allowChangePlan
        primaryStakeholder
        providerFirstSegmentId
        providerSecondSegmentId
        savingsComparisonId
        defaultOraclePersonalityId
        billingProduct {
          id
          name
          platform
        }
        billingProvider {
          id
          name
        }
        billingProviderProductPrices {
          id
          price
          trialSegmentPrice
          currency
          percentSavings
          trialPercentSavings
        }
        billingProviderProductText {
          id
          language
          key
          value
          parameters
          isValueProp
          valuePropOrder
          enabled
          ecommerceSku
          parameterBillingProviderProductId
          parameterBillingProviderProduct {
            id
            sku
            description
          }
        }
        ecommerceSkuMappingBonusEligibilities {
          id
          ecommerceSku
          provider
          planSku
          startDate
          endDate
          description
          marketplaceCouponCode
          marketplaceCouponCodeExpiration
          retainOnPlanChange
          languages
          createdAt
          ecommerceSkuMapping {
            contentProvider {
              contentId
              content {
                id
                imageContentRelationships {
                  id
                  type
                  image {
                    id
                    url
                  }
                }
              }
            }
          }
        }
        billingProviderProductEligibilities {
          id
          billingProviderProductId
          countries
          countryOperator
          ecommerceSkus
          enabled
          endDate
          lifetimeValue
          lifetimeValueOperator
          maxPurchases
          monthsSinceLastPurchase
          planCategories
          planSkus
          ruleType
          scope
          skuCheckType
          skuOperator
          startDate
          tenure
          tenureOperator
          createdAt
          updatedAt
        }
      }
    }
  }
`

/**
 * Backs the user-billing "Change Plan" dropdown — replaces the REST `/v1/billing/plans`
 * feed for that tab.
 * - `providerFirstSegmentId` is the Zuora `productRatePlanId` (same value the REST feed
 *   returns as `id` and that `PUT /v1/user/plan` needs as `nextRatePlanId`; see
 *   `BillingHistory`'s downgrade check).
 * - `description` is the plan display name (there is no plain `name` field on the type).
 * - `billingProviderProductText` is fetched unfiltered; the Change Plan UI picks English
 *   `shortDetails` client-side and interpolates `${ price }` from the currency price row.
 * - `$currency` is the member's `currencyIso` from `GET /v1/user/subscription`; it filters
 *   `billingProviderProductPrices` server side. `null` returns every currency row and the
 *   caller picks one (USD fallback).
 */
export const GET_CHANGE_PLAN_OPTIONS = gql`
  query GetChangePlanOptions($skus: [String!]!, $currency: String) {
    billingProviderProductList(where: { sku: { in: $skus } }) {
      billingProviderProducts {
        sku
        description
        providerFirstSegmentId
        billingProviderProductPrices(currency: $currency) {
          price
          trialSegmentPrice
          currency
          currencySymbol
        }
        billingProviderProductText {
          language
          key
          value
          parameters
          enabled
        }
      }
    }
  }
`

export const GET_PLAN_BY_ID = gql`
  query GetPlanById($id: ID!) {
    billingProviderProductList(where: { id: { eq: $id } }) {
      billingProviderProducts {
        id
        sku
        description
        enabled
        termLength
        termType
        trialLength
        trialTermType
        billingProviderProductPrices {
          id
          price
          trialSegmentPrice
          currency
        }
        billingProviderProductEligibilities {
          id
          billingProviderProductId
          countries
          countryOperator
          ecommerceSkus
          enabled
          endDate
          lifetimeValue
          lifetimeValueOperator
          maxPurchases
          monthsSinceLastPurchase
          planCategories
          planSkus
          ruleType
          scope
          skuCheckType
          skuOperator
          startDate
          tenure
          tenureOperator
          createdAt
          updatedAt
        }
      }
    }
  }
`

export const GET_PLANS_BY_IDS = gql`
  query GetPlansByIds($ids: [ID!]!) {
    billingProviderProductList(where: { id: { in: $ids } }) {
      billingProviderProducts {
        id
        sku
        description
        termLength
        termType
        trialLength
        trialTermType
        billingProviderProductPrices {
          id
          price
          trialSegmentPrice
          currency
        }
      }
    }
  }
`

export const GET_PLANS_BY_SEARCH_STRING = gql`
  query GetPlansList($limit: Int!, $searchString: String!) {
    planList: billingProviderProductList(
      order: [["createdAt", "DESC"]]
      limit: $limit
      billingProvider: { name: { in: [itunes, zuora, google, amazon, roku, paypal, myo_paypal] } }
      searchString: $searchString
    ) {
      billingProviderProducts {
        id
        sku
        description
        enabled
      }
    }
  }
`

export const GET_PLAN_TEXT_TRANSLATIONS = gql`
  query BillingProviderProductTextTranslate(
    $text: String!
    $languages: [ProviderProductLanguage]!
    $formality: TranslationFormality!
    $checkForExistingTranslations: Boolean!
    $parameters: [String]
  ) {
    billingProviderProductTextTranslate(
      input: {
        text: $text
        languages: $languages
        checkForExistingTranslations: $checkForExistingTranslations
        parameters: $parameters
      }
      translationOptions: { preserveFormatting: true, formality: $formality }
    ) {
      translations {
        language
        value
      }
    }
  }
`

export const GET_PLAN_GRID_LIST = gql`
  query BillingPlanGridList($limit: Int!, $offset: Int!, $order: [[String]]!) {
    billingPlanGridList(limit: $limit, offset: $offset, order: $order) {
      billingPlanGrids {
        id
        page
        defaultPageGrid
        priority
        enabled
        variantName
        newMemberBillingProviderProductId1
        newMemberBillingProviderProductId2
        newMemberBillingProviderProductId3
        newMemberBillingProviderProductId4
        existingMemberBillingProviderProductId1
        existingMemberBillingProviderProductId2
        existingMemberBillingProviderProductId3
        existingMemberBillingProviderProductId4
        startDate
        endDate
        createdAt
      }
    }
    allPlanGrids: billingPlanGridList(limit: 10000) {
      billingPlanGrids {
        id
      }
    }
  }
`

export const GET_PLAN_GRID_BY_ID = gql`
  query BillingPlanGridById($id: ID!) {
    billingPlanGridList(where: { id: { eq: $id } }) {
      billingPlanGrids {
        id
        page
        defaultPageGrid
        priority
        enabled
        variantName
        newMemberBillingProviderProductId1
        newMemberBillingProviderProductId2
        newMemberBillingProviderProductId3
        newMemberBillingProviderProductId4
        newMemberBestValueBillingProviderProductId
        existingMemberBillingProviderProductId1
        existingMemberBillingProviderProductId2
        existingMemberBillingProviderProductId3
        existingMemberBillingProviderProductId4
        existingMemberBestValueBillingProviderProductId
        countryIn
        countryNotIn
        currencyIn
        currencyNotIn
        deviceTypeIn
        deviceTypeNotIn
        languageIn
        languageNotIn
        billingProviderProductIdIn
        billingProviderProductIdNotIn
        billingProductIdIn
        billingProductIdNotIn
        tenure
        tenureOperator
        lifetimeValue
        lifetimeValueOperator
        startDate
        endDate
      }
    }
  }
`

export const GET_PLAN_CHANGELOG_LIST = gql`
  query BillingProductChangeLogList(
    $order: [[String]]!
    $limit: Int!
    $offset: Int!
    $where: BillingProductChangeLogListWhereInput
  ) {
    changeLog: billingProductChangeLogList(
      order: $order
      limit: $limit
      offset: $offset
      where: $where
    ) {
      billingProductChangeLogs {
        id
        userInfoUuid
        username
        changeType
        entityName
        providerName
        productName
        sku
        discountCode
        pricingCurrency
        textLanguage
        textKey
        planGridPage
        planGridVariant
        previousValues
        newValues
        createdAt
        updatedAt
      }
    }
    allChangeLogs: billingProductChangeLogList(where: $where, limit: 1000) {
      billingProductChangeLogs {
        id
      }
    }
  }
`

export const GET_PLAN_CHANGELOG_BY_SKU = gql`
  query BillingProductChangeLogList(
    $sku: String!
    $order: [[String]]!
    $limit: Int!
    $offset: Int!
  ) {
    changeLog: billingProductChangeLogList(
      where: { sku: { eq: $sku } }
      order: $order
      limit: $limit
      offset: $offset
    ) {
      billingProductChangeLogs {
        id
        userInfoUuid
        username
        changeType
        entityName
        providerName
        productName
        sku
        discountCode
        pricingCurrency
        textLanguage
        textKey
        planGridPage
        planGridVariant
        previousValues
        newValues
        createdAt
        updatedAt
      }
    }
    allChangeLogs: billingProductChangeLogList(where: { sku: { eq: $sku } }, limit: 1000) {
      billingProductChangeLogs {
        id
      }
    }
  }
`

/** Same tables/shape as {@link GET_PLAN_CHANGELOG_BY_SKU}; filters by discount code for billing discount audit rows. */
export const GET_PLAN_CHANGELOG_BY_DISCOUNT_CODE = gql`
  query BillingProductChangeLogListByDiscountCode(
    $discountCode: String!
    $order: [[String]]!
    $limit: Int!
    $offset: Int!
  ) {
    changeLog: billingProductChangeLogList(
      where: { discountCode: { eq: $discountCode } }
      order: $order
      limit: $limit
      offset: $offset
    ) {
      billingProductChangeLogs {
        id
        userInfoUuid
        username
        changeType
        entityName
        providerName
        productName
        sku
        discountCode
        pricingCurrency
        textLanguage
        textKey
        planGridPage
        planGridVariant
        previousValues
        newValues
        createdAt
        updatedAt
      }
    }
    allChangeLogs: billingProductChangeLogList(
      where: { discountCode: { eq: $discountCode } }
      limit: 1000
    ) {
      billingProductChangeLogs {
        id
      }
    }
  }
`

/** Same tables/shape as {@link GET_PLAN_CHANGELOG_BY_SKU}; filters by plan grid page + variant. */
export const GET_PLAN_CHANGELOG_BY_PLAN_GRID = gql`
  query BillingProductChangeLogListByPlanGrid(
    $planGridPage: String!
    $planGridVariant: String!
    $order: [[String]]!
    $limit: Int!
    $offset: Int!
  ) {
    changeLog: billingProductChangeLogList(
      where: { planGridPage: { eq: $planGridPage }, planGridVariant: { eq: $planGridVariant } }
      order: $order
      limit: $limit
      offset: $offset
    ) {
      billingProductChangeLogs {
        id
        userInfoUuid
        username
        changeType
        entityName
        providerName
        productName
        sku
        discountCode
        pricingCurrency
        textLanguage
        textKey
        planGridPage
        planGridVariant
        previousValues
        newValues
        createdAt
        updatedAt
      }
    }
    allChangeLogs: billingProductChangeLogList(
      where: { planGridPage: { eq: $planGridPage }, planGridVariant: { eq: $planGridVariant } }
      limit: 1000
    ) {
      billingProductChangeLogs {
        id
      }
    }
  }
`

export const GET_RELATED_PLANS = gql`
  query RelatedBillingProviderProductList($id: ID!) {
    relatedBillingProviderProductList(id: $id) {
      billingProviderProducts {
        id
        billingProviderId
        billingProductId
        providerFirstSegmentId
        providerSecondSegmentId
        sku
        type
        termLength
        termType
        description
        trialLength
        trialTermType
        taxable
        checkoutEnabledStart
        checkoutEnabledEnd
        checkoutEnabled
        enabled
        savingsComparisonId
        allowChangePlan
        primaryStakeholder
      }
    }
  }
`

export const GET_BONUS_LIST = gql`
  query EcommerceSkuMappingsList(
    $limit: Int!
    $offset: Int!
    $order: [[String]]!
    $where: EcommerceSkuMappingsWhereInput
    $searchString: String
  ) {
    ecommerceSkuMappingsList(
      where: $where
      order: $order
      limit: $limit
      offset: $offset
      searchString: $searchString
    ) {
      ecommerceSkuMappings {
        ecommerceSku
        contentTitleGroupIds
        title
        enabled
        doNotEntitle
        contentRelated {
          id
          contentId
          contentType
          language
        }
        contentProvider {
          contentId
          provider
          content {
            id
            contentId
            contentType
            title
            language
            contentReferencesTo(types: ["product"]) {
              id
              contentFrom {
                id
                contentId
                contentType
                title
                language
                published
              }
            }
          }
        }
        createdAt
        updatedAt
      }
    }
    allBonuses: ecommerceSkuMappingsList(where: $where, limit: 10000, searchString: $searchString) {
      ecommerceSkuMappings {
        ecommerceSku
      }
    }
  }
`

export const GET_BONUS_BY_ECOMMERCE_SKU = gql`
  query EcommerceSkuMappingsList($where: EcommerceSkuMappingsWhereInput!) {
    ecommerceSkuMappingsList(where: $where) {
      ecommerceSkuMappings {
        ecommerceSku
        contentTitleGroupIds
        contentSetId
        contentSetStartDate
        contentSetEndDate
        contentSetDurationSeconds
        title
        notes
        displayNameEn
        displayNameEs
        displayNameFr
        displayNameDe
        descriptionEn
        descriptionEs
        descriptionFr
        descriptionDe
        enabled
        contentProviderKey
        contentRelated {
          id
          contentId
          contentType
          title
          language
          published
          contentAssetReferences(contentAssetReferences: { type: "worksheet" }) {
            asset {
              originalFileName
              language
              renderUrl
              mimeType
              uuid
            }
          }
        }
        contentProvider {
          contentId
          provider
          content {
            id
            contentId
            contentType
            title
            language
            imageContentRelationships {
              id
              type
              image {
                id
                url
              }
            }
            contentAssetReferences(contentAssetReferences: { type: "worksheet" }) {
              asset {
                originalFileName
                language
                renderUrl
                mimeType
                uuid
              }
            }
          }
        }
        contentProviders {
          contentId
          provider
          content {
            id
            contentId
            contentType
            title
            language
            contentAssetReferences(contentAssetReferences: { type: "worksheet" }) {
              asset {
                originalFileName
                language
                renderUrl
                mimeType
                uuid
              }
            }
          }
        }
        ecommerceSkuMappingBonusEligibilities {
          id
          planSku
          startDate
          endDate
        }
      }
    }
  }
`

export const GET_BONUS_BY_ECOMMERCE_SKU_SEARCH = gql`
  query EcommerceSkuMappingsList($searchString: String!) {
    ecommerceSkuMappingsList(
      where: { provider: { eq: "GAIA_BONUS" } }
      searchString: $searchString
    ) {
      ecommerceSkuMappings {
        ecommerceSku
        enabled
        contentProvider {
          contentId
          content {
            id
            imageContentRelationships {
              id
              type
              image {
                id
                url
              }
            }
          }
        }
        contentProviders {
          contentId
          content {
            id
            language
          }
        }
      }
    }
  }
`

export const GET_BONUS_ELIGIBILITIES = gql`
  query EcommerceSkuMappingBonusEligibilitiesList(
    $where: EcommerceSkuMappingBonusEligibilitiesWhereInput
    $effectiveDate: DateTime
  ) {
    ecommerceSkuMappingBonusEligibilitiesList(where: $where, effectiveDate: $effectiveDate) {
      ecommerceSkuMappingBonusEligibilities {
        planSku
        description
        ecommerceSku
        marketplaceCouponCode
        marketplaceCouponCodeExpiration
        ecommerceSkuMapping {
          ecommerceSku
          contentProviders {
            contentId
            content {
              contentId
              contentAssetReferences(contentAssetReferences: { type: "worksheet" }) {
                asset {
                  originalFileName
                  language
                  renderUrl
                  mimeType
                  uuid
                }
              }
            }
          }
          contentRelated {
            title
            contentId
            language
            contentAssetReferences(contentAssetReferences: { type: "worksheet" }) {
              asset {
                originalFileName
                language
                renderUrl
                mimeType
                uuid
              }
            }
          }
        }
      }
    }
  }
`

export const GET_BONUS_CAMPAIGN_LIST = gql`
  query EcommerceSkuMappingBonusEligibilitiesList(
    $order: [[String]]!
    $offset: Int!
    $limit: Int!
    $searchString: String
  ) {
    ecommerceSkuMappingBonusEligibilitiesList(
      order: $order
      offset: $offset
      limit: $limit
      searchString: $searchString
    ) {
      ecommerceSkuMappingBonusEligibilities {
        id
        ecommerceSku
        planSku
        startDate
        endDate
        ecommerceSkuMapping {
          ecommerceSku
          enabled
        }
        billingProviderProduct {
          id
          sku
          enabled
        }
      }
    }
    allBonusCampaigns: ecommerceSkuMappingBonusEligibilitiesList(
      limit: 10000
      searchString: $searchString
    ) {
      ecommerceSkuMappingBonusEligibilities {
        id
      }
    }
  }
`

export const GET_BILLING_PRODUCTS = gql`
  query GetBillingProducts(
    $limit: Int
    $offset: Int
    $order: [[String]]
    $where: BillingProductListWhereInput
  ) {
    billingProductList(limit: $limit, offset: $offset, order: $order, where: $where) {
      billingProducts {
        id
        name
        platform
        contentSetId
      }
    }
  }
`

export const GET_ZUORA_ANNUAL_PLANS = gql`
  query GetZuoraAnnualPlans {
    billingProviderProductList(
      billingProvider: { name: { in: [zuora] } }
      where: { enabled: { eq: true }, termLength: { eq: 1 }, termType: { eq: year } }
    ) {
      billingProviderProducts {
        id
        sku
        providerFirstSegmentId
        description
        termType
        termLength
        trialLength
        trialTermType
        enabled
        billingProduct {
          id
          name
        }
        billingProvider {
          id
          name
        }
      }
    }
  }
`

export const GET_BILLING_PROVIDER_PRODUCT_LIST_BY_TERMS = gql`
  query GetBillingProviderProductListByTerms(
    $termLength: Int
    $termType: ProviderProductTermType
    $trialLength: Int
    $trialTermType: ProviderProductTermType
  ) {
    billingProviderProductList(
      where: {
        termLength: { eq: $termLength }
        termType: { eq: $termType }
        trialLength: { eq: $trialLength }
        trialTermType: { eq: $trialTermType }
        enabled: { eq: true }
      }
      billingProvider: { name: { in: [zuora] } }
    ) {
      billingProviderProducts {
        id
        sku
        termLength
        termType
        trialLength
        trialTermType
        billingProviderProductText {
          id
          language
          key
          value
          parameters
        }
      }
    }
  }
`

export const GET_BILLING_PRODUCT_CHANGELOG_USERS = gql`
  query BillingProductChangeLogUsers {
    billingProductChangeLogUsers {
      users {
        uuid
        username
      }
    }
  }
`

export const GET_ORACLE_PERSONALITY_LIST = gql`
  query OraclePersonalityList(
    $limit: Int = 100
    $offset: Int = 0
    $order: [[String!]!] = [["key", "ASC"]]
    $where: OraclePersonalityListWhere = { enabled: { eq: true } }
  ) {
    oraclePersonalityList(limit: $limit, offset: $offset, order: $order, where: $where) {
      oraclePersonalities {
        id
        key
        isDefault
      }
    }
  }
`

/** Billing discount fields shared by list + mutations (admin discount form). */
const BILLING_DISCOUNT_FIELDS = `
  id
  code
  description
  enabled
  allowedForPlanChange
  percentageDiscount
  amountDiscount
  minimumPrice
  minimumTrialSegmentPrice
  billingCycles
  startDate
  endDate
  availableCount
  redemptionCount
  redemptionsPerUser
  redemptionsPerUserMonths
  countries
  countriesOperator
  currencies
  currenciesOperator
  languages
  languagesOperator
  skus
  skusOperator
  planCategories
  planCategoriesOperator
  ecommerceSkus
  ecommerceSkusOperator
  tenure
  tenureOperator
  lifetimeValue
  lifetimeValueOperator
  platform
  uids
  billingDiscountBulkId
  createdAt
  updatedAt
`

/** Bulk discount batch fields for list + detail views. */
const BULK_DISCOUNT_FIELDS = `
  id
  uuid
  description
  quantity
  codeLength
  discountsCreated
  success
  errors
  codes
  createdAt
  updatedAt
`

export const GET_BILLING_DISCOUNT_LIST = gql`
  query GetBillingDiscountList(
    $limit: Int
    $offset: Int
    $order: [[String]]
    $searchString: String
    $where: BillingDiscountListWhereInput
  ) {
    billingDiscountList(
      limit: $limit
      offset: $offset
      order: $order
      where: $where
      searchString: $searchString
    ) {
      billingDiscounts {
        ${BILLING_DISCOUNT_FIELDS}
      }
    }
    allDiscounts: billingDiscountList(
      limit: 10000
      offset: 0
      where: $where
      searchString: $searchString
    ) {
      billingDiscounts {
        id
      }
    }
  }
`

export const GET_BULK_DISCOUNT_LIST = gql`
  query GetBulkDiscountList(
    $limit: Int
    $offset: Int
    $order: [[String]]
    $searchString: String
    $where: BulkDiscountListWhereInput
  ) {
    bulkDiscountList(
      limit: $limit
      offset: $offset
      order: $order
      where: $where
      searchString: $searchString
    ) {
      bulkDiscounts {
        ${BULK_DISCOUNT_FIELDS}
        discounts {
          id
          percentageDiscount
          amountDiscount
          enabled
        }
      }
    }
    allBulkDiscounts: bulkDiscountList(
      limit: 10000
      offset: 0
      where: $where
      searchString: $searchString
    ) {
      bulkDiscounts {
        id
      }
    }
  }
`

export const GET_BILLING_DISCOUNT_BULK = gql`
  query GetBillingDiscountBulk($id: ID!) {
    billingDiscountBulk(id: $id) {
      ${BULK_DISCOUNT_FIELDS}
      discounts {
        ${BILLING_DISCOUNT_FIELDS}
      }
    }
  }
`

/** Resolve plan SKU → billingProduct.name for discount SKU / plan category consistency checks. */
export const GET_BILLING_PROVIDER_PRODUCTS_BY_SKUS = gql`
  query GetBillingProviderProductsBySkus($skus: [String!]!) {
    billingProviderProductList(where: { sku: { in: $skus } }) {
      billingProviderProducts {
        sku
        billingProduct {
          name
        }
      }
    }
  }
`
